import { useState } from "react";
import { ChevronDown, ChevronRight, ListPlus } from "lucide-react";
import { ROLE_LABELS } from "@/constants/workflow";

/** Workflow-driven pool of panels waiting to be assigned (Design Approved -> BIM, etc.). */
export function AssignablePanels({ pools, onCreateTask }) {
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState({});

  const toggle = (department, uid) =>
    setSelected((prev) => {
      const current = prev[department] || [];
      return {
        ...prev,
        [department]: current.includes(uid)
          ? current.filter((x) => x !== uid)
          : [...current, uid],
      };
    });

  const active = pools.filter((pool) => pool.open_count > 0);
  if (!active.length) return null;

  return (
    <section data-testid="assignable-section" className="mt-4 space-y-2">
      {active.map((pool) => {
        const expanded = open === pool.department;
        const picked = selected[pool.department] || [];
        const waiting = pool.panels.filter((p) => !p.assigned);
        return (
          <div
            key={pool.department}
            data-testid={`assignable-pool-${pool.department}`}
            className="rounded-xl border border-cyan-500/30 bg-white"
          >
            <button
              data-testid={`assignable-toggle-${pool.department}`}
              onClick={() => setOpen(expanded ? null : pool.department)}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left"
            >
              {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <span className="mono text-[10.5px] uppercase tracking-[0.16em] text-slate-700">
                {ROLE_LABELS[pool.department]} assignable tasks
              </span>
              <span className="mono rounded-full bg-cyan-500/10 px-2 py-0.5 text-[10px] text-cyan-700">
                {pool.open_count}
              </span>
              <span className="mono ml-auto hidden text-[10px] uppercase tracking-wider text-slate-400 sm:block">
                {pool.trigger}
              </span>
            </button>

            {expanded ? (
              <div className="border-t border-slate-100 px-4 py-3">
                <div className="okn-scroll max-h-44 overflow-y-auto">
                  <div className="flex flex-wrap gap-1.5">
                    {waiting.map((panel) => (
                      <button
                        key={panel.panel_uid}
                        data-testid={`assignable-panel-${panel.panel_uid}`}
                        onClick={() => toggle(pool.department, panel.panel_uid)}
                        className={`mono rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
                          picked.includes(panel.panel_uid)
                            ? "border-cyan-600 bg-cyan-600 text-white"
                            : "border-slate-200 bg-[#EEF2F8] text-slate-700 hover:border-cyan-400"
                        }`}
                      >
                        {panel.panel_name}
                        {panel.level ? ` · ${panel.level}` : ""}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    data-testid={`assignable-select-all-${pool.department}`}
                    onClick={() =>
                      setSelected((prev) => ({
                        ...prev,
                        [pool.department]:
                          picked.length === waiting.length
                            ? []
                            : waiting.map((p) => p.panel_uid),
                      }))
                    }
                    className="mono rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
                  >
                    {picked.length === waiting.length ? "Clear" : "Select all"}
                  </button>
                  <button
                    data-testid={`assignable-create-task-${pool.department}`}
                    disabled={!picked.length}
                    onClick={() => {
                      onCreateTask(pool, picked);
                      setSelected((prev) => ({ ...prev, [pool.department]: [] }));
                    }}
                    className="mono flex items-center gap-1.5 rounded-full bg-cyan-600 px-4 py-1.5 text-[10px] uppercase tracking-wider text-white hover:bg-cyan-700 disabled:opacity-40"
                  >
                    <ListPlus size={12} /> Create task ({picked.length})
                  </button>
                  {pool.total - pool.open_count ? (
                    <span className="mono text-[10px] text-slate-400">
                      {pool.total - pool.open_count} already in a task
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}
